import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FilterService, GridComponent } from '@syncfusion/ej2-angular-grids';
import { ProductService } from 'src/app/services/Product.service';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { EActionTypes, EToaster, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { GeneralService } from 'src/app/services/general.service';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';

@Component({
  selector: 'app-ops-product',
  standalone: true,
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, UserPermissionHandlerDirective, TooltipModule, LoaderComponent],
  providers: [FilterService],
  templateUrl: './ops-product.html',
  styleUrl: './ops-product.scss'
})

export class OpsProduct {
  isCollapsed: boolean = true;
  @ViewChild('gridProduct', { static: false }) public gridProduct: GridComponent;
  @ViewChild('productModal', { static: false }) productModal: ElementRef;

  activeTab: 'All' | 'Active' | 'Inactive' = 'All';
  productForm!: FormGroup;
  productCategory: { CatSeqCategoryId: number, CatTxtCategoryDescr: string }[] = [];
  data: any[] = [];
  allData: any[] = [];
  originalProduct: any = null;
  private destroy$ = new Subject<void>();
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.Product, Action: EActionTypes.VIEW };
  createPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.Product, Action: EActionTypes.CREATE };
  deletePermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.Product, Action: EActionTypes.DELETE };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.Product, Action: EActionTypes.EDIT };
  isFiltering: boolean = false;
  loaderIcon: Promise<boolean> = Promise.resolve(false);

  constructor(
    private fb: FormBuilder,
    private productService: ProductService,
    private generalService: GeneralService,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadProducts();
    this.loadProductCategories();
  }

  initForm(): void {
    this.productForm = this.fb.group({
      ProductId: [null],
      ProductName: ['', [Validators.required, Validators.maxLength(30)]],
      ProductCategoryId: [null, [Validators.required]],
      ProductInactive: [false],
      ProductDeleteFlag: [false]
    });
  }

  get editRight(): boolean {
    return this.generalService.getUserPermissionsForModule(EUserSecurityFunctionID.Product).EditRight;
  }

  loadProducts(): void {
    this.loaderIcon = Promise.resolve(true);
    this.productService.getProducts().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (Array.isArray(res)) {
          this.allData = res
            .filter((item: any) => item.Status !== null)
            .map((item: any) => ({
              id: item.ProductId,
              name: item.ProductName,
              category: item.CategoryName,
              categoryId: item.ProductCategoryId,
              status: item.Status ? 'Inactive' : 'Active'
            }));
          // this.applyFilter(this.activeTab);
          this.onTabChange(this.activeTab);
        } else {
          this.allData = [];
          this.data = [];
        }
        this.loaderIcon = Promise.resolve(false);
      },
      error: err => {
        this.loaderIcon = Promise.resolve(false);
        this.toastr.error('Error fetching products', 'Error', this.sharedHTMLSettings.ToasterConfig);
        this.allData = [];
        this.data = [];
      }
    });
  }

  loadProductCategories(): void {
    this.productService.GetAllProductCategories().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (Array.isArray(res)) {
          this.productCategory = res
            .filter((item: any) => item.CatBlnCategoryInactive === false || item.CatBlnCategoryInactive === null)
            .map(item => ({
              CatSeqCategoryId: item.CatSeqCategoryId,
              CatTxtCategoryDescr: item.CatTxtCategoryDescr
            }));
        } else {
          console.warn('Unexpected response:', res);
          this.productCategory = [];
        }
      },
      error: err => {
        this.toastr.error('Error loading categories', 'Error', this.sharedHTMLSettings.ToasterConfig);
        this.productCategory = [];
      }
    });
  }

  deleteProduct(productId: number): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This product will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (result.isConfirmed) {
        this.loaderIcon = Promise.resolve(true);
        this.productService.getProducts().pipe(takeUntil(this.destroy$)).subscribe({
          next: (products: any[]) => {
            const product = products.find(p => p.ProductId === productId);
            if (!product) {
              this.loaderIcon = Promise.resolve(false);
              this.toastr.error('Product not Found', 'Error', this.sharedHTMLSettings.ToasterConfig);
              return;
            }
            const productData = {
              ProductId: product.ProductId,
              ProductName: product.ProductName,
              ProductCategoryId: product.ProductCategoryId,
              ProductInactive: product.Status,
              ProductDeleteFlag: true
            };
            this.productService.saveProduct(productData).subscribe({
              next: (response: any) => {
                const message = response?.Message || 'Product deleted successfully.';
                const isSuccess = response?.Success ?? true;

                if (isSuccess) {
                  this.toastr.success(message, 'Success', this.sharedHTMLSettings.ToasterConfig);
                  this.loadProducts();
                } else {
                  this.toastr.error(message, 'Warning', this.sharedHTMLSettings.ToasterConfig);
                }

                this.loadProducts();
                this.loaderIcon = Promise.resolve(false);
              },
              error: () => {
                this.loaderIcon = Promise.resolve(false);
                this.toastr.error('Error deleting Product', 'Error', this.sharedHTMLSettings.ToasterConfig);
              }
            });
          },
          error: () => {
            this.loaderIcon = Promise.resolve(false);
            this.toastr.error('Error loading products before delete', 'Error', this.sharedHTMLSettings.ToasterConfig);
          }
        });
      }
    });
  }

  onActiveChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.productForm.get('ProductInactive')?.setValue(!checked);
  }

  saveProduct(): void {
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();

      const missingFields: string[] = [];
      if (this.productForm.get('ProductName')?.hasError('required')) {
        missingFields.push('Product Name');
      }
      if (this.productForm.get('ProductCategoryId')?.hasError('required')) {
        missingFields.push('Product Category');
      }
      if (missingFields.length > 0) {
        this.toastr.warning(
          `Please fill: ${missingFields.join(', ')}`,
          'Validation',
          this.sharedHTMLSettings.ToasterConfig
        );
      }
      return;
    }

    const formValue = this.productForm.value;

    const currentProduct = {
      ProductId: formValue.ProductId,
      ProductName: formValue.ProductName?.trim(),
      ProductCategoryId: formValue.ProductCategoryId,
      ProductInactive: formValue.ProductInactive
    };

    if (
      this.originalProduct &&
      JSON.stringify(currentProduct) === JSON.stringify(this.originalProduct)
    ) {
      this.toastr.info('No changes', 'Info', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    const enteredName = formValue.ProductName.trim().toLowerCase();
    const isDuplicate = this.allData.some(
      p => p.name.trim().toLowerCase() === enteredName && p.id !== formValue.ProductId
    );

    if (isDuplicate) {
      this.toastr.warning(
        `Product with name "${formValue.ProductName}" already exists`,
        'Validation',
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }
    const productData: any = {
      ProductName: formValue.ProductName.trim(),
      ProductCategoryId: formValue.ProductCategoryId,
      ProductInactive: formValue.ProductInactive,
      ProductDeleteFlag: false
    };

    if (formValue.ProductId) {
      productData.ProductId = formValue.ProductId;
    }

    Swal.fire({
      title: $localize`Confirm Save`,
      text: formValue.ProductId
        ? $localize`Are you sure you want to update this product?`
        : $localize`Are you sure you want to add this product?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`Yes`,
      cancelButtonText: $localize`No`
    }).then((result) => {
      if (result.isConfirmed) {
        this.loaderIcon = Promise.resolve(true);
        this.closeModalById('productModal');
        // if (productData.ProductId) {
        //   console.log(`Updated product with ID: ${productData.ProductId}`, productData);
        // } else {
        //   console.log(`Created new product`, productData);
        // }
        this.productService.saveProduct(productData).pipe(takeUntil(this.destroy$)).subscribe({
          next: (response) => {
            this.loaderIcon = Promise.resolve(false);
            if (response && response.Success) {
              this.toastr.success(
                response.Message || $localize`Product saved successfully`,
                $localize`Success`,
                this.sharedHTMLSettings.ToasterConfig
              );

              this.loadProducts();
              this.productForm.reset({
                ProductId: null,
                ProductName: '',
                ProductCategoryId: null,
                ProductInactive: false,
                ProductDeleteFlag: false
              });
            } else {
              this.toastr.error(
                response?.Message || $localize`Failed to save product`,
                $localize`Error`,
                this.sharedHTMLSettings.ToasterConfig
              );
            }
          },
          error: (err) => {
            this.loaderIcon = Promise.resolve(false);
            this.toastr.error(
              err?.message || $localize`An unexpected error occurred`,
              $localize`Error`,
              this.sharedHTMLSettings.ToasterConfig
            );
          }
        });

      }
    });
  }

  onProductRowSelected(event: any): void {
    const data = event?.data;
    if (!data) {
      console.warn('Row selected event has no data:', event);
      return;
    }

    if (!this.editRight) {
      this.toastr.info(
        EToaster.MsgInfoNoRightToEdit + $localize`:@@Product:Product`,
        EToaster.TitleInfo,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const selectedProduct = {
      ProductId: data.ProductId ?? data.id ?? null,
      ProductName: data.ProductName ?? data.name ?? '',
      ProductCategoryId: data.ProductCategoryId ?? data.categoryId ?? null,
      ProductInactive: (data.ProductInactive !== undefined)
        ? data.ProductInactive
        : (data.status === 'Inactive')
    };

    this.originalProduct = { ...selectedProduct };
    this.productForm.patchValue(selectedProduct);
    this.productForm.markAsPristine();
    this.productForm.markAsUntouched();
    this.openProductModal(false);
    setTimeout(() => {
    });
  }

  resetProductFormWithDefaults(): void {
    this.productForm.reset();
    this.productForm.markAsPristine();
    this.productForm.markAsUntouched();
  }

  closeModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const modalInstance = window['bootstrap'].Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
    const backdrops = document.getElementsByClassName('modal-backdrop');
    while (backdrops.length > 0) {
      backdrops[0].parentNode?.removeChild(backdrops[0]);
    }
    document.body.classList.remove('modal-open');
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  openProductModal(resetForm: boolean = true): void {
    if (resetForm) {
      this.resetProductFormWithDefaults();
    }
    this.loadProductCategories();
    const modalEl = document.getElementById('productModal');
    if (modalEl) {
      let modal = window['bootstrap'].Modal.getInstance(modalEl);
      if (!modal) {
        modal = new window['bootstrap'].Modal(modalEl, { backdrop: 'static', keyboard: false });
      }
      modal.show();
    }
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  async onTabChange(tab: 'All' | 'Active' | 'Inactive') {
    this.activeTab = tab;
    this.gridProduct.showSpinner();
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        if (tab === 'Active') {
          this.data = this.allData.filter(i => i.status === 'Active');
        } else if (tab === 'Inactive') {
          this.data = this.allData.filter(i => i.status === 'Inactive');
        } else {
          this.data = [...this.allData];
        }
        resolve();
      }, 0);
    });

    this.gridProduct.hideSpinner();
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

  public created(args1) {
    this.gridProduct.element.querySelectorAll('.e-filterdiv')[this.gridProduct.getColumnIndexByField('category')].classList.add('e-hide');
    this.gridProduct.element.querySelectorAll('.e-filterdiv')[this.gridProduct.getColumnIndexByField('activeInactive')].classList.add('e-hide');
    this.gridProduct.element.querySelectorAll('.e-filterdiv')[this.gridProduct.getColumnIndexByField('Delete')].classList.add('e-hide');
    this.gridProduct.filterSettings = { mode: 'Immediate', immediateModeDelay: 100 };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}